import React, { useState, useEffect, useMemo } from 'react';
import { usePermissionCheck } from '@/hooks/usePermissionCheck';
import { Plus, Edit, Trash2, Search, MapPin, Globe, Clock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { formatDisplayDate } from '@/lib/utils';
import { State, StateFormData, Country } from '@/types/geography';
import { useConfirmDialog, createDeleteConfirm, createDeactivateConfirm } from '@/components/ui/confirm-dialog';
import DataTablePagination from '@/components/ui/data-table-pagination';
import {
  useStates as useStatesApi,
  useState as useStateApi,
  useCreateState,
  useUpdateState,
  useDeleteState,
} from '@/api-services/hooks/useStateRefactored';
import { useCountries as useCountriesApi } from '@/api-services/hooks/useCountryRefactored';
import {
  buildStatePayload,
  mapStateMasterToState,
  mapStateToPayload,
  mapStatusToApi,
  mapCountryMasterToCountry,
} from '@/utils/geographyMappers';
import { API_BASE_URL } from '@/api-services/config';
import type { StateMaster } from '@/api-services/hooks/types';
const StatesPage: React.FC = () => {
  const can = usePermissionCheck();
  const [states, setStates] = useState<State[]>([]);
  const [filteredStates, setFilteredStates] = useState<State[]>([]);
  const [countries, setCountries] = useState<Country[]>([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingState, setEditingState] = useState<State | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [countryFilter, setCountryFilter] = useState<string>('all');

  // Ref to maintain focus on search input
  const searchInputRef = React.useRef<HTMLInputElement>(null);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  const { confirm, ConfirmDialog } = useConfirmDialog();

  // Form state
  const [formData, setFormData] = useState<StateFormData>({
    name: '',
    code: '',
    countryId: '',
    status: 'active'
  });

  // Validation errors state
  const [errors, setErrors] = useState<{
    name?: string;
    code?: string;
    countryId?: string;
    status?: string;
  }>({});

  // Validation function
  const validateForm = (): boolean => {
    const newErrors: typeof errors = {};

    // Name validation
    if (!formData.name.trim()) {
      newErrors.name = 'State name is required';
    } else {
      // Check for unique name (case-insensitive) globally across all countries
      const isDuplicateName = states.some(
        state => state.name.toLowerCase() === formData.name.toLowerCase() &&
        state.id !== editingState?.id
      );
      if (isDuplicateName) {
        newErrors.name = 'State with this name already exists';
      }
    }

    // Code validation
    if (!formData.code.trim()) {
      newErrors.code = 'State code is required';
    } else if (formData.code.length < 2) {
      newErrors.code = 'State code must be at least 2 characters';
    } else if (formData.code.length > 5) {
      newErrors.code = 'State code must be no more than 5 characters';
    } else {
      // Check for unique code (case-insensitive) globally across all countries
      const isDuplicateCode = states.some(
        state => state.code.toLowerCase() === formData.code.toLowerCase() &&
        state.id !== editingState?.id
      );
      if (isDuplicateCode) {
        newErrors.code = 'State with this code already exists';
      }
    }

    // Country validation
    if (!formData.countryId) {
      newErrors.countryId = 'Country is required';
    }

    // Status validation
    if (!formData.status) {
      newErrors.status = 'Status is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Real-time validation on input change
  const handleInputChange = (field: keyof StateFormData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));

    // Clear error for this field when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  // Debounce search term (same as Base Pricing: reset page when debounced value updates)
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
      setCurrentPage(1);
    }, 300);

    return () => {
      clearTimeout(timer);
    };
  }, [searchTerm]);

  // Only search if term is more than 2 characters (3+)
  const shouldSearch = debouncedSearchTerm.trim().length > 2;

  const stateQueryParams = useMemo(() => ({
    page: 0,
    size: 1000,
    ...(shouldSearch && { search: debouncedSearchTerm.trim() }),
  }), [shouldSearch, debouncedSearchTerm]);
  const countryQueryParams = useMemo(() => ({ page: 0, size: 1000 }), []);

  const {
    data: stateResponse,
    loading: statesLoading,
    error: statesError,
    refetch: refetchStates,
  } = useStatesApi(stateQueryParams);

  // Auto-focus search input on page load (after loading completes)
  useEffect(() => {
    if (!statesLoading && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [statesLoading]);

  const {
    data: countryResponse,
    loading: countriesLoading,
    error: countriesError,
  } = useCountriesApi(countryQueryParams);

  const createStateMutation = useCreateState();
  const updateStateMutation = useUpdateState();
  const deleteStateMutation = useDeleteState();

  // Fetch existing state data when editing to preserve system IP fields
  const { data: existingStateData } = useStateApi(
    editingState ? Number(editingState.id) : 0,
    {
      enabled: !!editingState && !!editingState.id,
    }
  );

  const actionLoading =
    createStateMutation.loading ||
    updateStateMutation.loading ||
    deleteStateMutation.loading;

  useEffect(() => {
    if (stateResponse?.content) {
      const mappedStates = stateResponse.content.map(mapStateMasterToState);
      setStates(mappedStates);
    } else {
      setStates([]);
    }
  }, [stateResponse]);

  useEffect(() => {
    if (countryResponse?.content) {
      const mappedCountries = countryResponse.content.map(mapCountryMasterToCountry);
      setCountries(mappedCountries);
    } else {
      setCountries([]);
    }
  }, [countryResponse]);

  useEffect(() => {
    if (statesError) {
      toast.error(statesError.message);
    }
  }, [statesError]);

  useEffect(() => {
    if (countriesError) {
      toast.error(countriesError.message);
    }
  }, [countriesError]);

  // Filter states by status and country only (search is done by API when shouldSearch, like Base Pricing)
  useEffect(() => {
    let filtered = states;

    if (statusFilter !== 'all') {
      filtered = filtered.filter(state => state.status === statusFilter);
    }

    if (countryFilter !== 'all') {
      filtered = filtered.filter(state => state.countryId === countryFilter);
    }

    setFilteredStates(filtered);
    setCurrentPage(1); // Reset to first page when filters change
  }, [states, statusFilter, countryFilter]);

  // Pagination calculations
  const totalItems = filteredStates.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedStates = filteredStates.slice(startIndex, endIndex);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleItemsPerPageChange = (newItemsPerPage: number) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1); // Reset to first page when items per page changes
  };

  const resetForm = () => {
    setFormData({
      name: '',
      code: '',
      countryId: '',
      status: 'active'
    });
    setErrors({});
    setEditingState(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate form
    if (!validateForm()) {
      toast.error('Please fix the validation errors');
      return;
    }

    try {
      if (editingState) {
        const payload = await buildStatePayload(formData, editingState.id, existingStateData || undefined);
        await updateStateMutation.mutate({
          id: Number(editingState.id),
          data: payload,
        });
        toast.success('State updated successfully!');
        refetchStates();
      } else {
        const payload = await buildStatePayload(formData);
        await createStateMutation.mutate(payload);
        toast.success('State created successfully!');
        // Refresh list after create
        refetchStates();
      }

      setIsDialogOpen(false);
      resetForm();
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to save state';

      // Handle server-side validation errors for duplicates
      if (message.toLowerCase().includes('duplicate') ||
          message.toLowerCase().includes('already exists') ||
          message.toLowerCase().includes('unique')) {
        if (message.toLowerCase().includes('name')) {
          setErrors(prev => ({ ...prev, name: 'State name already exists' }));
        } else if (message.toLowerCase().includes('code')) {
          setErrors(prev => ({ ...prev, code: 'State code already exists' }));
        } else {
          // Generic duplicate error
          setErrors(prev => ({
            ...prev,
            name: 'Duplicate state detected',
            code: 'Duplicate state detected'
          }));
        }
      }

      toast.error(message);
    }
  };

  const handleEdit = (state: State) => {
    setEditingState(state);
    setFormData({
      name: state.name,
      code: state.code,
      countryId: state.countryId,
      status: state.status,
      metadata: state.metadata || {}
    });
    setIsDialogOpen(true);
  };

  const handleDelete = (state: State) => {
    if (actionLoading) return;
    const stateName = state.name;
    confirm(createDeleteConfirm(
      'State',
      async () => {
        try {
          await deleteStateMutation.mutate(Number(state.id));
          toast.success(`${stateName} deleted successfully!`);
          refetchStates();
        } catch (error) {
          const message = error instanceof Error ? error.message : 'Failed to delete state';
          toast.error(message);
        }
      },
      `Are you sure you want to delete ${stateName}? This will permanently remove the state and all associated data. This action cannot be undone.`
    ));
  };

  const handleStatusToggle = (state: State) => {
    if (actionLoading) return;
    const isActive = state.status === 'active';
    const stateName = state.name;

    confirm(createDeactivateConfirm(
      'State',
      isActive,
      async () => {
        try {
          const newStatus = isActive ? 'inactive' : 'active';

          // Fetch existing state data to preserve system IP fields
          let existingStateData: StateMaster | undefined;
          try {
            const response = await fetch(`${API_BASE_URL}/state/${state.id}`);
            if (response.ok) {
              const apiResponse = await response.json();
              existingStateData = apiResponse.data;
            }
          } catch (err) {
            // Silently handle fetch error
          }
          const basePayload = await mapStateToPayload(state, existingStateData);
          const payload = {
            ...basePayload,
            status: mapStatusToApi(newStatus),
          };
          await updateStateMutation.mutate({
            id: Number(state.id),
            data: payload,
          });
          toast.success(`${stateName} ${newStatus === 'active' ? 'activated' : 'deactivated'} successfully!`);
          refetchStates();
        } catch (error) {
          const message = error instanceof Error ? error.message : 'Failed to update state status';
          toast.error(message);
        }
      }
    ));
  };

  const getCountryName = (countryId: string) => {
    const country = countries.find(c => c.id === countryId);
    return country ? country.name : 'Unknown';
  };

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-foreground">States</h1>
          <p className="text-muted-foreground mt-1">Manage states and provinces</p>
        </div>
        <div className="flex gap-2">
          <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            {can('Create States') && (
              <DialogTrigger asChild>
                <Button className="bg-gradient-primary hover:opacity-90" onClick={resetForm}>
                  <Plus className="w-4 h-4 mr-2" />
                  Add State
                </Button>
              </DialogTrigger>
            )}
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>
                {editingState ? 'Edit State' : 'Add New State'}
              </DialogTitle>
              <DialogDescription>
                {editingState
                  ? 'Update the state information below.'
                  : 'Add a new state or province to the system.'
                }
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium">State/Province Name <span className="text-red-500">*</span></label>
                  <Input
                    key="state-name-input"
                    value={formData.name}
                    onChange={(e) => handleInputChange('name', e.target.value)}
                    onBlur={() => {
                      // Validate on blur for better UX
                      if (formData.name.trim()) {
                        const isDuplicateName = states.some(
                          state => state.name.toLowerCase() === formData.name.toLowerCase() &&
                          state.id !== editingState?.id
                        );
                        if (isDuplicateName) {
                          setErrors(prev => ({ ...prev, name: 'State with this name already exists' }));
                        }
                      }
                    }}
                    placeholder="California"
                    className={errors.name ? "border-red-500 focus:ring-red-500" : ""}
                    required
                  />
                  {errors.name && (
                    <p className="text-sm text-red-600 mt-1">{errors.name}</p>
                  )}
                </div>
                <div>
                  <label className="text-sm font-medium">Code <span className="text-red-500">*</span></label>
                  <Input
                    key="state-code-input"
                    value={formData.code}
                    onChange={(e) => handleInputChange('code', e.target.value.toUpperCase())}
                    onBlur={() => {
                      // Validate on blur for better UX
                      if (formData.code.trim()) {
                        if (formData.code.length < 2) {
                          setErrors(prev => ({ ...prev, code: 'State code must be at least 2 characters' }));
                        } else if (formData.code.length > 5) {
                          setErrors(prev => ({ ...prev, code: 'State code must be no more than 5 characters' }));
                        } else {
                          const isDuplicateCode = states.some(
                            state => state.code.toLowerCase() === formData.code.toLowerCase() &&
                            state.id !== editingState?.id
                          );
                          if (isDuplicateCode) {
                            setErrors(prev => ({ ...prev, code: 'State with this code already exists' }));
                          }
                        }
                      }
                    }}
                    placeholder="CA"
                    maxLength={5}
                    className={errors.code ? "border-red-500 focus:ring-red-500" : ""}
                    required
                  />
                  {errors.code && (
                    <p className="text-sm text-red-600 mt-1">{errors.code}</p>
                  )}
                </div>
              </div>

              <div>
                <label className="text-sm font-medium">Country <span className="text-red-500">*</span></label>
                <Select
                  value={formData.countryId}
                  onValueChange={(value) => {
                    handleInputChange('countryId', value);
                  }}
                >
                  <SelectTrigger disabled={countriesLoading || countries.length === 0} className={errors.countryId ? "border-red-500 focus:ring-red-500" : ""}>
                    <SelectValue placeholder="Select a country" />
                  </SelectTrigger>
                  <SelectContent>
                    {countries.map((country) => (
                      <SelectItem key={country.id} value={country.id}>
                        {country.name} ({country.code})
                      </SelectItem>
                    ))}
                    {!countriesLoading && countries.length === 0 && (
                      <SelectItem value="no-country" disabled>
                        No countries available
                      </SelectItem>
                    )}
                  </SelectContent>
                </Select>
                {errors.countryId && (
                  <p className="text-sm text-red-600 mt-1">{errors.countryId}</p>
                )}
              </div>

              <div>
                <label className="text-sm font-medium">Status <span className="text-red-500">*</span></label>
                <Select
                  value={formData.status}
                  onValueChange={(value: 'active' | 'inactive') => {
                    handleInputChange('status', value);
                  }}
                >
                  <SelectTrigger className={errors.status ? "border-red-500 focus:ring-red-500" : ""}>
                    <SelectValue placeholder="Select status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="inactive">Inactive</SelectItem>
                  </SelectContent>
                </Select>
                {errors.status && (
                  <p className="text-sm text-red-600 mt-1">{errors.status}</p>
                )}
              </div>

              <div className="flex justify-end space-x-2 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setIsDialogOpen(false)}
                >
                  Cancel
                </Button>
                <Button type="submit" disabled={actionLoading}>
                  {actionLoading ? 'Saving...' : (editingState ? 'Update' : 'Create')}
                </Button>
              </div>
            </form>
          </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Filters and Search */}
      <Card>
        <CardContent className="p-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                ref={searchInputRef}
                type="search"
                placeholder="Search states... (min 3 characters)"
                className="pl-10"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') e.preventDefault();
                }}
                autoComplete="off"
              />
            </div>
            <Select value={countryFilter} onValueChange={setCountryFilter}>
              <SelectTrigger className="w-40">
                <SelectValue placeholder="All Countries" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Countries</SelectItem>
                {countries.map((country) => (
                  <SelectItem key={country.id} value={country.id}>
                    {country.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-40">
                <SelectValue placeholder="All Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="inactive">Inactive</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* States Table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <MapPin className="w-5 h-5" />
            States ({statesLoading ? '...' : filteredStates.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>State/Province</TableHead>
                  <TableHead>Code</TableHead>
                  <TableHead>Country</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Created</TableHead>
                  {(can('Edit States') || can('Delete States')) && <TableHead className="text-right">Actions</TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {statesLoading ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                      Loading states...
                    </TableCell>
                  </TableRow>
                ) : paginatedStates.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                      {filteredStates.length === 0
                        ? 'No states found'
                        : 'No states match the current filters'}
                    </TableCell>
                  </TableRow>
                ) : (
                  paginatedStates.map((state) => (
                    <TableRow key={state.id} className="hover:bg-muted/50">
                      <TableCell>
                        <div>
                          <div className="font-medium">{state.name}</div>
                          {state.metadata?.capital && (
                            <div className="text-sm text-muted-foreground">
                              Capital: {state.metadata.capital}
                            </div>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline">{state.code}</Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Globe className="w-4 h-4 text-muted-foreground" />
                          {getCountryName(state.countryId)}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge
                          variant={state.status === 'active' ? 'default' : 'secondary'}
                          className={state.status === 'active' ? 'bg-success text-success-foreground cursor-pointer' : 'bg-muted text-muted-foreground cursor-pointer'}
                          onClick={() => handleStatusToggle(state)}
                        >
                          {state.status}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="text-sm text-muted-foreground">
                          {formatDisplayDate(state.createdAt)}
                        </div>
                      </TableCell>
                      {(can('Edit States') || can('Delete States')) && (
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-2">
                            {can('Edit States') && (
                              <Button variant="ghost" size="sm" onClick={() => handleEdit(state)}>
                                <Edit className="h-4 w-4" />
                              </Button>
                            )}
                            {can('Delete States') && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDelete(state)}
                                disabled={actionLoading}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      )}
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>

            {!statesLoading && totalPages > 0 && (
              <DataTablePagination
                currentPage={currentPage}
                totalPages={totalPages}
                totalItems={totalItems}
                itemsPerPage={itemsPerPage}
                onPageChange={handlePageChange}
                onItemsPerPageChange={handleItemsPerPageChange}
                className="mt-4"
              />
            )}
          </div>
        </CardContent>
      </Card>

      {/* Confirmation Dialog */}
      <ConfirmDialog />
    </div>
  );
};

export default StatesPage;
